import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import CountUp from 'react-countup';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  FaMapMarkerAlt,
  FaGlobe,
  FaBook,
  FaClock,
  FaInbox,
  FaEnvelope,
  FaSpinner,
  FaChartPie,
  FaChartBar,
  FaExclamationCircle
} from 'react-icons/fa';
import {
  getDashboardStats,
  getPlacesStatusStats,
  getJournalStatusStats,
  getFeedbackStats
} from '../services/analytics.service';

// Stat Card Component with CountUp animation
const StatCard = ({ title, value, icon: Icon, color, subLabel, subValue }) => {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{title}</p>
          <p className="text-3xl font-bold text-slate-900 dark:text-white">
            <CountUp end={value} duration={1.5} separator="," />
          </p>
          {subLabel && (
            <p className="text-sm text-slate-400 mt-1">
              {subLabel}: <span className="font-medium text-slate-600">{subValue}</span>
            </p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
};

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-slate-900 px-4 py-2 rounded-lg shadow-lg border border-slate-200 dark:border-slate-800">
        <p className="font-medium text-slate-900 dark:text-white">{payload[0].name}</p>
        <p className="text-sm text-slate-600">{payload[0].value} items</p>
      </div>
    );
  }
  return null;
};

// Custom Legend for Pie Chart
const CustomLegend = ({ payload }) => {
  return (
    <div className="flex flex-wrap justify-center gap-4 mt-4">
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-sm text-slate-600">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

// Empty State Component
const EmptyChart = ({ title }) => (
  <div className="flex flex-col items-center justify-center h-64 text-slate-400">
    <FaExclamationCircle className="w-10 h-10 mb-3" />
    <p className="font-medium">No data available</p>
    <p className="text-sm">{title}</p>
  </div>
);

function Analytics() {
  const [stats, setStats] = useState({
    places: { total: 0, published: 0, draft: 0, archived: 0 },
    journals: { total: 0, pending: 0, approved: 0, rejected: 0 },
    feedback: { total: 0, new: 0, read: 0, responded: 0 }
  });
  const [placesChartData, setPlacesChartData] = useState([]);
  const [journalsChartData, setJournalsChartData] = useState([]);
  const [feedbackChartData, setFeedbackChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAllData = useCallback(async () => {
    try {
      setIsLoading(true);

      const [dashboardStats, placesStats, journalsStats, feedbackStats] = await Promise.all([
        getDashboardStats(),
        getPlacesStatusStats(),
        getJournalStatusStats(),
        getFeedbackStats()
      ]);

      setStats(dashboardStats);
      setPlacesChartData(placesStats);
      setJournalsChartData(journalsStats);
      setFeedbackChartData(feedbackStats);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <FaSpinner className="w-10 h-10 text-indigo-600 animate-spin" />
          <p className="text-slate-500 dark:text-slate-400 font-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const hasNoData =
    stats.places.total === 0 &&
    stats.journals.total === 0 &&
    stats.feedback.total === 0;

  if (hasNoData) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Analytics</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Platform overview and insights</p>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-100 flex items-center justify-center">
            <FaChartPie className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">No Data Yet</h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            Start adding places, journals, and collecting feedback to see analytics here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Analytics</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Platform overview and insights</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatCard
          title="Total Places"
          value={stats.places.total}
          icon={FaMapMarkerAlt}
          color="bg-indigo-500"
        />
        <StatCard
          title="Published Places"
          value={stats.places.published}
          icon={FaGlobe}
          color="bg-emerald-500"
          subLabel="Draft"
          subValue={stats.places.draft}
        />
        <StatCard
          title="Total Journals"
          value={stats.journals.total}
          icon={FaBook}
          color="bg-violet-500"
        />
        <StatCard
          title="Pending Journals"
          value={stats.journals.pending}
          icon={FaClock}
          color="bg-amber-500"
          subLabel="Approved"
          subValue={stats.journals.approved}
        />
        <StatCard
          title="New Feedback"
          value={stats.feedback.new}
          icon={FaEnvelope}
          color="bg-blue-500"
          subLabel="Total"
          subValue={stats.feedback.total}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Places by Status - Pie Chart */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <FaChartPie className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">Places by Status</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Content distribution</p>
            </div>
          </div>

          {placesChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={placesChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={4}
                  dataKey="value"
                  animationBegin={0}
                  animationDuration={1000}
                >
                  {placesChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart title="Add places to see distribution" />
          )}
        </div>

        {/* Journals by Status - Bar Chart */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <FaChartBar className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">Journals Moderation</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Review status breakdown</p>
            </div>
          </div>

          {journalsChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={journalsChartData}
                margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="value"
                  radius={[8, 8, 0, 0]}
                  animationBegin={0}
                  animationDuration={1000}
                >
                  {journalsChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart title="No journals to display" />
          )}
        </div>
      </div>

      {/* Feedback Distribution */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <FaInbox className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Feedback Status</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Inbox activity overview</p>
          </div>
        </div>

        {feedbackChartData.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {feedbackChartData.map((item, index) => (
              <div
                key={index}
                className="relative overflow-hidden rounded-xl p-6"
                style={{ backgroundColor: `${item.color}15` }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">{item.name}</p>
                    <p className="text-3xl font-bold mt-1" style={{ color: item.color }}>
                      <CountUp end={item.value} duration={1.5} />
                    </p>
                  </div>
                  <div
                    className="w-16 h-16 rounded-full opacity-20"
                    style={{ backgroundColor: item.color }}
                  />
                </div>
                {/* Progress bar */}
                <div className="mt-4 h-2 bg-white dark:bg-slate-900/50 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{
                      backgroundColor: item.color,
                      width: `${stats.feedback.total > 0 ? (item.value / stats.feedback.total) * 100 : 0}%`
                    }}
                  />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  {stats.feedback.total > 0
                    ? `${Math.round((item.value / stats.feedback.total) * 100)}% of total`
                    : '0%'}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <EmptyChart title="No feedback received yet" />
        )}
      </div>

      {/* Quick Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl p-6 text-white">
          <h4 className="font-semibold mb-2">Content Health</h4>
          <p className="text-indigo-100 text-sm mb-4">
            {stats.places.published > 0
              ? `${Math.round((stats.places.published / stats.places.total) * 100)}% of places are published`
              : 'No published places yet'}
          </p>
          <div className="h-2 bg-white dark:bg-slate-900/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white dark:bg-slate-900 rounded-full transition-all duration-1000"
              style={{
                width: `${stats.places.total > 0 ? (stats.places.published / stats.places.total) * 100 : 0}%`
              }}
            />
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white">
          <h4 className="font-semibold mb-2">Moderation Status</h4>
          <p className="text-emerald-100 text-sm mb-4">
            {stats.journals.pending > 0
              ? `${stats.journals.pending} journals awaiting review`
              : 'All journals reviewed'}
          </p>
          <div className="h-2 bg-white dark:bg-slate-900/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white dark:bg-slate-900 rounded-full transition-all duration-1000"
              style={{
                width: `${stats.journals.total > 0 ? (stats.journals.approved / stats.journals.total) * 100 : 0}%`
              }}
            />
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
          <h4 className="font-semibold mb-2">Response Rate</h4>
          <p className="text-blue-100 text-sm mb-4">
            {stats.feedback.responded > 0
              ? `${Math.round((stats.feedback.responded / stats.feedback.total) * 100)}% feedback responded`
              : 'No responses yet'}
          </p>
          <div className="h-2 bg-white dark:bg-slate-900/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white dark:bg-slate-900 rounded-full transition-all duration-1000"
              style={{
                width: `${stats.feedback.total > 0 ? (stats.feedback.responded / stats.feedback.total) * 100 : 0}%`
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Analytics;

